import DocsLayout, { CodeBlock, Callout } from '../../components/docs/DocsLayout';

export default function DocsPlugins() {
  return (
    <DocsLayout 
      title="Plugins" 
      description="Extend agent capabilities with the plugin system"
    >
      <section className="space-y-6">
        <p className="text-white/70 leading-relaxed">
          Plugins are modular capabilities that define what operations your agent can perform. By default, all plugins are disabled. You explicitly enable only the functionality your agent needs.
        </p>

        <Callout type="info" title="Default-Deny Security">
          AIBL follows a default-deny model. Agents cannot perform any action unless it's explicitly enabled through a plugin with configured permissions.
        </Callout>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Available Plugins</h2>

        <div className="space-y-6">
          <div className="p-6 rounded-xl bg-white/5 border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-2">Swap</h3>
            <p className="text-white/60 text-sm mb-4">Execute token swaps on supported decentralized exchanges.</p>
            <div className="space-y-2 text-sm">
              <p className="text-white/50">Configuration options:</p>
              <ul className="space-y-1 text-white/70">
                <li>• <strong>Max Slippage</strong>: Maximum allowed price slippage (0.1% - 5%)</li>
                <li>• <strong>Max Amount</strong>: Maximum swap value per transaction</li>
                <li>• <strong>Allowed DEXs</strong>: Whitelist of supported protocols</li>
              </ul>
            </div>
          </div>

          <div className="p-6 rounded-xl bg-white/5 border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-2">Transfer</h3>
            <p className="text-white/60 text-sm mb-4">Send tokens to specified addresses.</p>
            <div className="space-y-2 text-sm">
              <p className="text-white/50">Configuration options:</p>
              <ul className="space-y-1 text-white/70">
                <li>• <strong>Max Amount</strong>: Maximum transfer value</li>
                <li>• <strong>Daily Limit</strong>: Total daily transfer cap</li>
                <li>• <strong>Whitelisted Addresses</strong>: Approved recipient addresses</li>
              </ul>
            </div>
          </div>

          <div className="p-6 rounded-xl bg-white/5 border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-2">Bridge</h3>
            <p className="text-white/60 text-sm mb-4">Move assets across different blockchains.</p>
            <div className="space-y-2 text-sm">
              <p className="text-white/50">Configuration options:</p>
              <ul className="space-y-1 text-white/70">
                <li>• <strong>Max Amount</strong>: Maximum bridge transaction value</li>
                <li>• <strong>Allowed Chains</strong>: Permitted destination chains</li>
                <li>• <strong>Allowed Bridges</strong>: Approved bridge protocols</li>
              </ul>
            </div>
          </div>

          <div className="p-6 rounded-xl bg-white/5 border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-2">Wrap</h3>
            <p className="text-white/60 text-sm mb-4">Wrap and unwrap native tokens (ETH to WETH).</p>
            <div className="space-y-2 text-sm">
              <p className="text-white/50">Configuration options:</p>
              <ul className="space-y-1 text-white/70">
                <li>• <strong>Max Amount</strong>: Maximum wrap/unwrap value</li>
                <li>• <strong>Allowed Actions</strong>: wrap, unwrap, or both</li>
              </ul>
            </div>
          </div>

          <div className="p-6 rounded-xl bg-white/5 border border-white/10">
            <h3 className="text-lg font-semibold text-white mb-2">Buy Tokens</h3>
            <p className="text-white/60 text-sm mb-4">Purchase specific tokens from approved lists.</p>
            <div className="space-y-2 text-sm">
              <p className="text-white/50">Configuration options:</p>
              <ul className="space-y-1 text-white/70">
                <li>• <strong>Max Amount</strong>: Maximum purchase value</li>
                <li>• <strong>Allowed Tokens</strong>: Whitelist of purchasable tokens</li>
              </ul>
            </div>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Configuring Plugins</h2>
        
        <p className="text-white/70 mb-4">
          Plugins are configured per-agent through the dashboard or API. Here's an example configuration:
        </p>

        <CodeBlock language="json">
{`{
  "plugin": "swap",
  "enabled": true,
  "config": {
    "maxSlippage": 1,
    "maxAmount": "1000000000000000000",
    "allowedDEXs": ["uniswap", "sushiswap", "curve"]
  }
}`}
        </CodeBlock>

        <h2 className="text-2xl font-bold text-white mt-12 mb-6">Plugin API</h2>
        
        <h3 className="text-xl font-semibold text-white mt-8 mb-4">Get Plugin States</h3>
        <CodeBlock language="bash">
{`GET /v1/agents/{agentId}/plugins
Authorization: Bearer {apiKey}`}
        </CodeBlock>

        <h3 className="text-xl font-semibold text-white mt-8 mb-4">Update Plugin</h3>
        <CodeBlock language="bash">
{`PATCH /v1/agents/{agentId}/plugins/{pluginName}
Authorization: Bearer {apiKey}
Content-Type: application/json

{
  "enabled": true,
  "config": { ... }
}`}
        </CodeBlock>

        <Callout type="warning" title="Config Changes">
          Plugin configuration changes take effect immediately. Make sure to test in a safe environment before deploying to production.
        </Callout>
      </section>
    </DocsLayout>
  );
}
